import React, { useState, useEffect } from 'react';
import Logo from '../../components/common/Logo';
import { Star, Calendar, ThumbsUp, Eye, EyeOff, User } from 'lucide-react';
import DoctorSidebar from '../../components/doctor/DoctorSidebar';
import DoctorFeedbackService from '../../services/doctor-feedback.service';
import AuthService from '../../services/auth.service';
import { formatDate } from '../../utils/dateUtils';


const DoctorFeedbackPage = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalFeedback: 0,
    averageRating: 0,
    totalSessions: 0
  });

  useEffect(() => {
    loadFeedbacks();
  }, []);

  const loadFeedbacks = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Ottieni l'ID del therapist dal localStorage
      const user = AuthService.getUserFromStorage();
      const therapistId = user?.therapist?.id;
      
      if (!therapistId) {
        throw new Error('ID del terapeuta non trovato');
      }

      const response = await DoctorFeedbackService.getDoctorFeedbacks(therapistId);
      
      const feedbacksList = response.feedbacks || [];
      setFeedbacks(feedbacksList);
      
      // Calcola le statistiche
      setStats({
        totalFeedback: feedbacksList.length,
        averageRating: response.average_rating || 0,
        totalSessions: calculateTotalSessions(feedbacksList)
      });
      
    } catch (err) {
      console.error('Errore nel caricamento dei feedback:', err);
      setError('Non è stato possibile caricare i feedback. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalSessions = (feedbacksList) => {
    // Per ora usiamo una stima basata sui feedback
    // In futuro potresti aggiungere un campo session_count al feedback
    return feedbacksList.length * 3; // Stima media di 3 sessioni per feedback
  };


  const getPatientDisplayName = (feedback) => {
    if (feedback.patient?.user) {
      const { first_name, last_name } = feedback.patient.user;
      // Mostra solo l'iniziale del cognome per privacy
      return `${first_name} ${last_name.charAt(0)}.`;
    }
    return 'Paziente Anonimo';
  };

  const renderStars = (rating, size = 'w-4 h-4') => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`${size} ${
          i < rating
            ? 'text-yellow-400 fill-current'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <div className="w-64 bg-white shadow-md">
          <div className="p-4">
            <Logo />
          </div>
          <DoctorSidebar />
        </div>
        <div className="flex-1 bg-[#FAF4EB] p-8 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-600 mb-4"></div>
            <p className="text-gray-600">Caricamento feedback...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <div className="w-64 bg-white shadow-md">
        <div className="p-4">
          <Logo />
        </div>
        <DoctorSidebar />
      </div>

      <div className="flex-1 bg-[#FAF4EB] p-8">
        <header className="mb-8">
          <h1 className="text-2xl font-bold">Feedback Ricevuti</h1>
          <p className="text-gray-600">Gestisci e analizza i feedback dei tuoi pazienti</p>
        </header>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-8">
            <p>{error}</p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">Media Valutazioni</p>
                <p className="text-2xl font-bold">{(parseFloat(stats.averageRating) || 0).toFixed(1)}/5</p>
              </div>
              <div className="flex">
                {renderStars(Math.round(parseFloat(stats.averageRating) || 0), 'w-5 h-5')}
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">Feedback Totali</p>
                <p className="text-2xl font-bold">{stats.totalFeedback}</p>
              </div>
              <ThumbsUp className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600">Sessioni Stimate</p>
                <p className="text-2xl font-bold">{stats.totalSessions}</p>
              </div>
              <Calendar className="w-8 h-8 text-purple-600" />
            </div>
          </div>cd
        </div>

        {/* Feedback List */}
        <div className="bg-white rounded-lg shadow-md">
          {feedbacks.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nessun feedback ricevuto
              </h3>
              <p className="text-gray-500">
                I tuoi pazienti non hanno ancora lasciato feedback.
              </p>
            </div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold">
                  Tutti i Feedback ({feedbacks.length})
                </h2>
              </div>
              
              {feedbacks.map((feedback, index) => (
                <div
                  key={feedback.id}
                  className={`p-6 ${
                    index !== feedbacks.length - 1 ? 'border-b border-gray-100' : ''
                  } hover:bg-gray-50 transition-colors`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                        <User className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {getPatientDisplayName(feedback)}
                        </h3>
                        <div className="flex items-center mt-1">
                          <div className="flex mr-2">
                            {renderStars(feedback.rating)}
                          </div>
                          <span className="text-sm text-gray-600">
                            {formatDate(feedback.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {feedback.is_published ? (
                        <div className="flex items-center text-green-600">
                          <Eye className="w-4 h-4 mr-1" />
                          <span className="text-xs">Pubblicato</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-gray-500">
                          <EyeOff className="w-4 h-4 mr-1" />
                          <span className="text-xs">Non pubblicato</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {feedback.comment && (
                    <div className="ml-13">
                      <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                        &ldquo;{feedback.comment}&rdquo;
                      </p>
                    </div>
                  )}
                  
                  {!feedback.comment && (
                    <div className="ml-13">
                      <p className="text-gray-400 italic">
                        Nessun commento aggiunto
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        {/* Informazioni aggiuntive */}
        {feedbacks.length > 0 && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ThumbsUp className="w-5 h-5 text-blue-600 mt-0.5" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Informazioni sui Feedback
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    I feedback sono sempre visibili una volta inviati. Gli amministratori possono rimuovere eventuali contenuti inappropriati se necessario.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorFeedbackPage;